import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface EditProfileProps {
  user: {
    id: number
    name: string
    email: string
    preferred_affiliate_provider?: string | null
  }
  onClose: () => void
  onSuccess: (updatedUser: { id: number; name: string; email: string; preferred_affiliate_provider?: string | null }) => void
}

export default function EditProfile({ user, onClose, onSuccess }: EditProfileProps) {
  const [name, setName] = useState(user.name)
  const [provider, setProvider] = useState<string>(user.preferred_affiliate_provider || 'bookshop')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [showPinSetup, setShowPinSetup] = useState(false)
  const [hasPin, setHasPin] = useState(false)
  const [pinLoading, setPinLoading] = useState(false)
  const [pinError, setPinError] = useState('')
  const [pinSuccess, setPinSuccess] = useState(false)
  const [pin, setPin] = useState('')
  const [pinConfirmation, setPinConfirmation] = useState('')
  const [pinStatusLoaded, setPinStatusLoaded] = useState(false)

  useEffect(() => {
    fetch('/api/v1/pin_status')
      .then(res => res.json())
      .then(data => {
        setHasPin(data.has_pin)
        setPinStatusLoaded(true)
      })
      .catch(() => setPinStatusLoaded(true))
  }, [])

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPinError('')
    setPinSuccess(false)

    if (pin !== pinConfirmation) {
      setPinError('PINs do not match')
      return
    }

    setPinLoading(true)

    try {
      const response = await fetchWithCsrf('/api/v1/pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, pin_confirmation: pinConfirmation })
      })

      if (response.ok) {
        setPinSuccess(true)
        setHasPin(true)
        setPin('')
        setPinConfirmation('')
        setTimeout(() => {
          setShowPinSetup(false)
          setPinSuccess(false)
        }, 2000)
      } else {
        const data = await response.json()
        setPinError(data.errors?.join(', ') || data.error || 'Failed to set PIN')
      }
    } catch {
      setPinError('Failed to set PIN')
    } finally {
      setPinLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      setError('Name cannot be empty')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await fetchWithCsrf('/api/v1/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            name: name.trim(),
            preferred_affiliate_provider: provider
          }
        })
      })

      if (response.ok) {
        const data = await response.json()
        onSuccess(data.user)
        onClose()
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to update profile')
      }
    } catch (err) {
      setError('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)

    if (newPassword !== newPasswordConfirmation) {
      setPasswordError('New passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters')
      return
    }

    setPasswordLoading(true)

    try {
      const response = await fetchWithCsrf('/api/v1/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            current_password: currentPassword,
            password: newPassword,
            password_confirmation: newPasswordConfirmation
          }
        })
      })

      if (response.ok) {
        setPasswordSuccess(true)
        setCurrentPassword('')
        setNewPassword('')
        setNewPasswordConfirmation('')
        setTimeout(() => {
          setShowPasswordChange(false)
          setPasswordSuccess(false)
        }, 2000)
      } else {
        const data = await response.json()
        setPasswordError(data.errors?.join(', ') || 'Failed to change password')
      }
    } catch (err) {
      setPasswordError('Failed to change password')
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="md"
      showCloseButton={true}
    >
      <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Edit Profile</h2>
            <p className="text-gray-600 text-sm">Update your profile information</p>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                placeholder="Enter your name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Book Provider
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Where "Buy Book" buttons will link to
              </p>
              <div className="space-y-2">
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50">
                  <input
                    type="radio"
                    name="provider"
                    value="bookshop"
                    checked={provider === 'bookshop'}
                    onChange={(e) => setProvider(e.target.value)}
                    className="mr-3 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Bookshop.org</div>
                    <div className="text-xs text-gray-500">Support your local bookstores</div>
                  </div>
                </label>
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50">
                  <input
                    type="radio"
                    name="provider"
                    value="amazon"
                    checked={provider === 'amazon'}
                    onChange={(e) => setProvider(e.target.value)}
                    className="mr-3 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Amazon</div>
                    <div className="text-xs text-gray-500">Not local, but often lower prices</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Password Change Section */}
        <div className="border-t border-gray-200 p-6">
          {!showPasswordChange ? (
            <button
              type="button"
              onClick={() => setShowPasswordChange(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              Change Password
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>

              {passwordSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-600 text-sm">Password changed successfully!</p>
                </div>
              )}

              {passwordError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{passwordError}</p>
                </div>
              )}

              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password *
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  placeholder="••••••••"
                  required
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  placeholder="••••••••"
                  minLength={6}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label htmlFor="newPasswordConfirmation" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password *
                </label>
                <input
                  id="newPasswordConfirmation"
                  type="password"
                  value={newPasswordConfirmation}
                  onChange={(e) => setNewPasswordConfirmation(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  placeholder="••••••••"
                  minLength={6}
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChange(false)
                    setCurrentPassword('')
                    setNewPassword('')
                    setNewPasswordConfirmation('')
                    setPasswordError('')
                    setPasswordSuccess(false)
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* PIN Setup Section */}
        {pinStatusLoaded && (
          <div className="border-t border-gray-200 p-6">
            {!showPinSetup ? (
              <button
                type="button"
                onClick={() => setShowPinSetup(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {hasPin ? 'Change PIN' : 'Set Up PIN'}
              </button>
            ) : (
              <form onSubmit={handlePinSubmit} className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {hasPin ? 'Change PIN' : 'Set Up PIN'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Use a 4-digit PIN for quick login from the app on your phone.
                </p>

                {pinSuccess && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-600 text-sm">PIN {hasPin ? 'changed' : 'set'} successfully!</p>
                  </div>
                )}

                {pinError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{pinError}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                    PIN *
                  </label>
                  <input
                    id="pin"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    placeholder="4-digit PIN"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="pinConfirmation" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm PIN *
                  </label>
                  <input
                    id="pinConfirmation"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinConfirmation}
                    onChange={(e) => setPinConfirmation(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    placeholder="Confirm PIN"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={pinLoading || pin.length !== 4 || pinConfirmation.length !== 4}
                    className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                  >
                    {pinLoading ? 'Saving...' : (hasPin ? 'Change PIN' : 'Set PIN')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPinSetup(false)
                      setPin('')
                      setPinConfirmation('')
                      setPinError('')
                      setPinSuccess(false)
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
